/**
 * Match Review System
 *
 * Compare planned drafts with actual OpenDota match data
 * Calculate accuracy and provide side-by-side comparison
 */

export class MatchReview {
  constructor(data = {}) {
    this.reviewId = data.reviewId || this.generateReviewId();
    this.draftId = data.draftId;
    this.matchId = data.matchId; // OpenDota match ID
    this.userId = data.userId;
    this.createdAt = data.createdAt || Date.now();

    // Planned draft data
    this.plannedDraft = data.plannedDraft || null;

    // Actual match data from OpenDota
    this.actualMatch = data.actualMatch || null;

    // Calculated accuracy
    this.accuracy = data.accuracy || null;

    // Comparison results
    this.comparison = data.comparison || null;
  }

  generateReviewId() {
    return `review_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  toJSON() {
    return {
      reviewId: this.reviewId,
      draftId: this.draftId,
      matchId: this.matchId,
      userId: this.userId,
      createdAt: this.createdAt,
      plannedDraft: this.plannedDraft,
      actualMatch: this.actualMatch,
      accuracy: this.accuracy,
      comparison: this.comparison,
    };
  }

  static fromJSON(data) {
    return new MatchReview(data);
  }
}

export class MatchReviewService {
  /**
   * Fetch match data from OpenDota API
   */
  static async fetchMatchData(matchId) {
    try {
      const response = await fetch(
        `https://api.opendota.com/api/matches/${matchId}`
      );

      if (!response.ok) {
        throw new Error(`OpenDota API error: ${response.status}`);
      }

      const matchData = await response.json();
      return this.parseMatchData(matchData);
    } catch (error) {
      console.error('Error fetching match data:', error);
      throw error;
    }
  }

  /**
   * Parse OpenDota match data into our format
   */
  static parseMatchData(matchData) {
    // Extract picks and bans from picks_bans array
    const radiantPicks = [];
    const radiantBans = [];
    const direPicks = [];
    const direBans = [];

    if (matchData.picks_bans) {
      matchData.picks_bans.forEach(pb => {
        const team = pb.team === 0 ? 'radiant' : 'dire';
        if (pb.is_pick) {
          if (team === 'radiant') {
            radiantPicks.push({
              heroId: pb.hero_id,
              order: pb.order,
            });
          } else {
            direPicks.push({
              heroId: pb.hero_id,
              order: pb.order,
            });
          }
        } else {
          if (team === 'radiant') {
            radiantBans.push({
              heroId: pb.hero_id,
              order: pb.order,
            });
          } else {
            direBans.push({
              heroId: pb.hero_id,
              order: pb.order,
            });
          }
        }
      });
    }

    // Sort by order
    const sortByOrder = (a, b) => a.order - b.order;
    radiantPicks.sort(sortByOrder);
    radiantBans.sort(sortByOrder);
    direPicks.sort(sortByOrder);
    direBans.sort(sortByOrder);

    return {
      matchId: matchData.match_id,
      radiantWin: matchData.radiant_win,
      duration: matchData.duration,
      radiantScore: matchData.radiant_score,
      direScore: matchData.dire_score,
      radiantPicks: radiantPicks.map(p => p.heroId),
      radiantBans: radiantBans.map(p => p.heroId),
      direPicks: direPicks.map(p => p.heroId),
      direBans: direBans.map(p => p.heroId),
      startTime: matchData.start_time,
      players: matchData.players || [],
    };
  }

  /**
   * Calculate draft accuracy between planned and actual
   */
  static calculateAccuracy(plannedDraft, actualMatch) {
    let totalPositions = 0;
    let matchedPositions = 0;

    // Helper to compare arrays
    const compareArrays = (planned, actual) => {
      let matched = 0;
      let total = Math.max(planned.length, actual.length);

      planned.forEach((heroId, index) => {
        if (heroId && heroId === actual[index]) {
          matched++;
        }
      });

      return { matched, total };
    };

    // Get the current scenario being compared
    const scenario = plannedDraft.scenarios?.[0] || {};
    const picks = scenario.picks || [];

    // Extract radiant and dire picks from planned draft
    // Assuming standard draft format: positions 0-4 are radiant, 5-9 are dire (5v5)
    // This needs to match your actual draft structure
    const plannedRadiantPicks = picks.slice(0, 5).filter(h => h);
    const plannedDirePicks = picks.slice(5, 10).filter(h => h);

    // Compare picks
    const radiantComparison = compareArrays(
      plannedRadiantPicks,
      actualMatch.radiantPicks
    );
    const direComparison = compareArrays(
      plannedDirePicks,
      actualMatch.direPicks
    );

    matchedPositions = radiantComparison.matched + direComparison.matched;
    totalPositions = radiantComparison.total + direComparison.total;

    const pickAccuracy = totalPositions > 0
      ? (matchedPositions / totalPositions) * 100
      : 0;

    // Calculate ban accuracy (if your draft includes bans)
    // This is optional - you may not have bans in your planned draft
    let banAccuracy = null;

    return {
      overall: Math.round(pickAccuracy * 10) / 10, // Round to 1 decimal
      picks: {
        matched: matchedPositions,
        total: totalPositions,
        accuracy: Math.round(pickAccuracy * 10) / 10,
      },
      radiant: {
        matched: radiantComparison.matched,
        total: radiantComparison.total,
        accuracy: radiantComparison.total > 0
          ? Math.round((radiantComparison.matched / radiantComparison.total) * 1000) / 10
          : 0,
      },
      dire: {
        matched: direComparison.matched,
        total: direComparison.total,
        accuracy: direComparison.total > 0
          ? Math.round((direComparison.matched / direComparison.total) * 1000) / 10
          : 0,
      },
      bans: banAccuracy,
    };
  }

  /**
   * Generate detailed comparison between planned and actual
   */
  static generateComparison(plannedDraft, actualMatch, accuracy) {
    const scenario = plannedDraft.scenarios?.[0] || {};
    const picks = scenario.picks || [];

    const plannedRadiantPicks = picks.slice(0, 5);
    const plannedDirePicks = picks.slice(5, 10);

    const differences = [];

    // Find differences in radiant picks
    plannedRadiantPicks.forEach((plannedHero, index) => {
      const actualHero = actualMatch.radiantPicks[index];
      if (plannedHero !== actualHero) {
        differences.push({
          team: 'radiant',
          position: index,
          planned: plannedHero || null,
          actual: actualHero || null,
          type: !plannedHero ? 'missing_in_plan' : !actualHero ? 'extra_in_plan' : 'different',
        });
      }
    });

    // Find differences in dire picks
    plannedDirePicks.forEach((plannedHero, index) => {
      const actualHero = actualMatch.direPicks[index];
      if (plannedHero !== actualHero) {
        differences.push({
          team: 'dire',
          position: index + 5,
          planned: plannedHero || null,
          actual: actualHero || null,
          type: !plannedHero ? 'missing_in_plan' : !actualHero ? 'extra_in_plan' : 'different',
        });
      }
    });

    return {
      matchResult: {
        winner: actualMatch.radiantWin ? 'radiant' : 'dire',
        duration: actualMatch.duration,
        score: `${actualMatch.radiantScore} - ${actualMatch.direScore}`,
      },
      differences,
      insights: this.generateInsights(accuracy, differences, actualMatch),
    };
  }

  /**
   * Generate insights from comparison
   */
  static generateInsights(accuracy, differences, actualMatch) {
    const insights = [];

    // Overall accuracy insight
    if (accuracy.overall >= 90) {
      insights.push({
        type: 'success',
        message: `Excellent prediction! Your draft was ${accuracy.overall}% accurate.`,
      });
    } else if (accuracy.overall >= 70) {
      insights.push({
        type: 'success',
        message: `Good prediction. Your draft was ${accuracy.overall}% accurate.`,
      });
    } else if (accuracy.overall >= 50) {
      insights.push({
        type: 'info',
        message: `Moderate accuracy at ${accuracy.overall}%. The actual draft deviated significantly from your plan.`,
      });
    } else {
      insights.push({
        type: 'warning',
        message: `Low accuracy at ${accuracy.overall}%. The actual draft was quite different from your prediction.`,
      });
    }

    // Team-specific insights
    if (accuracy.radiant.accuracy > accuracy.dire.accuracy + 20) {
      insights.push({
        type: 'info',
        message: `Your Radiant draft prediction (${accuracy.radiant.accuracy}%) was much more accurate than Dire (${accuracy.dire.accuracy}%).`,
      });
    } else if (accuracy.dire.accuracy > accuracy.radiant.accuracy + 20) {
      insights.push({
        type: 'info',
        message: `Your Dire draft prediction (${accuracy.dire.accuracy}%) was much more accurate than Radiant (${accuracy.radiant.accuracy}%).`,
      });
    }

    // Match result insight
    const winner = actualMatch.radiantWin ? 'Radiant' : 'Dire';
    const duration = Math.floor(actualMatch.duration / 60);
    insights.push({
      type: 'info',
      message: `${winner} won the match in ${duration} minutes with a score of ${actualMatch.radiantScore}-${actualMatch.direScore}.`,
    });

    // Number of differences
    if (differences.length === 0) {
      insights.push({
        type: 'success',
        message: 'Perfect match! Every pick matched your planned draft exactly.',
      });
    } else if (differences.length <= 2) {
      insights.push({
        type: 'success',
        message: `Only ${differences.length} pick(s) differed from your plan.`,
      });
    } else if (differences.length <= 5) {
      insights.push({
        type: 'info',
        message: `${differences.length} picks differed from your planned draft.`,
      });
    } else {
      insights.push({
        type: 'warning',
        message: `${differences.length} picks differed significantly from your plan.`,
      });
    }

    return insights;
  }

  /**
   * Create a match review
   */
  static async createMatchReview(draftId, plannedDraft, matchId, userId) {
    // Fetch actual match data
    const actualMatch = await this.fetchMatchData(matchId);

    // Calculate accuracy
    const accuracy = this.calculateAccuracy(plannedDraft, actualMatch);

    // Generate comparison
    const comparison = this.generateComparison(plannedDraft, actualMatch, accuracy);

    // Create review object
    const review = new MatchReview({
      draftId,
      matchId,
      userId,
      plannedDraft,
      actualMatch,
      accuracy,
      comparison,
    });

    return review;
  }
}

export default MatchReviewService;
